import { AfterViewInit, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { IPagination } from 'shared/types/pagination.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Account } from 'shared/types/payment-account.interface';
import { finalize } from 'rxjs';
import { TagComponent, TagComponentType } from 'shared/components/tag/tag.component';
import { TranslateModule } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { ActivatedRoute, Router } from '@angular/router';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { LocalStorageKeys } from 'shared/types/local-storage-keys.enum';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DocumentsReceivedComponent } from './documents-received/documents-received.component';
import { MaskPipe } from 'shared/pipes/mask.pipe';
import { RemoveAccountModalComponent } from './remove-account-modal/remove-account-modal.component';
import { tap } from 'rxjs/operators';
import { KorviuNoDataComponent } from 'shared/components/korviu-no-data/korviu-no-data.component';
import { HideTooltipOnScrollDirective } from 'shared/directives';

interface AccountStatusTag {
  localizableName: string;
  type: TagComponentType;
  status: string;
}

@Component({
  selector: 'app-summary',
  standalone: true,
  imports: [
    KorviuLoaderComponent,
    TagComponent,
    TranslateModule,
    NzButtonModule,
    NzIconModule,
    NzPopconfirmModule,
    HideTooltipOnScrollDirective,
    KorviuButtonComponent,
    MaskPipe,
    KorviuNoDataComponent
  ],
  templateUrl: './summary.component.html',
  styleUrl: './summary.component.scss'
})
export class SummaryComponent implements OnInit, AfterViewInit {
  public isLoading = signal<boolean>(false);
  public isReloading = signal<boolean>(false);
  public pagination: IPagination = { page: 1, pageSize: 10 };
  public accounts: Account[] = [];
  private _destroyRef = inject(DestroyRef);
  private _paymentAccountsService = inject(PaymentAccountsService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _modalService = inject(NzModalService);

  public ngOnInit(): void {
    this._loadPaymentAccounts();
  }

  public ngAfterViewInit(): void {
    this._openShowDocumentsReceivedModal();
  }

  public canBeSetByDefault(account: Account): boolean {
    return !account?.default && !(account?.overall_status === 'REVIEWING' || account?.overall_status === 'DECLINED');
  }

  public getAccountStatus(account: Account): AccountStatusTag | null {
    if (account?.default && !(account?.overall_status === 'REVIEWING' || account?.overall_status === 'DECLINED')) {
      return {
        localizableName: 'profile.default',
        type: 'info',
        status: account.overall_status
      };
    }
    if (account?.overall_status == 'REVIEWING') {
      return {
        localizableName: 'profile.reviewing',
        type: 'warning',
        status: account.overall_status
      };
    }
    if (account?.overall_status === 'DECLINED') {
      return {
        localizableName: 'profile.declined',
        type: 'error',
        status: account.overall_status
      };
    }
    return null;
  }

  public onSetAsDefaultCLick(id: number): void {
    this._paymentAccountsService
      .setPaymentAccountAsDefault(id)
      .pipe(
        tap(() => {
          const currentDefaultAccount = this.accounts.find(a => !!a.default);
          if (currentDefaultAccount) {
            currentDefaultAccount.default = false;
          }
          const newlyDefault = this.accounts.find(a => a.id === id);
          if (newlyDefault) {
            newlyDefault.default = true;
          }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public onDetailsCLick(id: number): void {
    this._router.navigate(['details', id], { relativeTo: this._route.parent });
  }

  public onRemoveCLick(id: number, isDefault: boolean): void {
    this._modalService
      .create({
        nzContent: RemoveAccountModalComponent,
        nzWrapClassName: 'modal-wrap',
        nzClosable: false,
        nzWidth: 554,
        nzData: { id, isDefault },
        nzClassName: 'modal-dialog'
      })
      .afterClose.pipe(
        tap((result: boolean) => {
          if (result) {
            this._loadPaymentAccounts(true);
          }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public onAddAccountClick(): void {
    this._router.navigate(['add'], { relativeTo: this._route.parent });
  }

  private _openShowDocumentsReceivedModal(): void {
    if (!!localStorage.getItem(LocalStorageKeys.SHOW_DOCUMENTS_RECEIVED_MODAL)) {
      this._modalService.create({
        nzContent: DocumentsReceivedComponent,
        nzWrapClassName: 'modal-wrap',
        nzClosable: false,
        nzWidth: 554,
        nzClassName: 'modal-dialog'
      });
    }
  }

  private _loadPaymentAccounts(reload?: boolean): void {
    this.isLoading.set(!reload);
    this.isReloading.set(!!reload);

    this._paymentAccountsService
      .getUserPaymentAccounts()
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.isReloading.set(false);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((accounts: Account[]) => {
        this.accounts = accounts;
      });
  }
}
